import { Link } from '../../../components/Link';
import { Seo } from '../../../lib/seo';
import { LegalLayout } from '../../../components/LegalLayout';
import { useLegal } from './useLegal';
import { useLegalPagesVisible } from '../../../lib/useLegalPagesVisible';

/** Plain-English GDPR summary — the "marketing" companion to the formal DPA. */
export function GdprPage() {
  const c = useLegal();
  const { isVisible } = useLegalPagesVisible();
  const subprocessorsPage = isVisible('subprocessors');
  return (
    <><Seo title="GDPR compliance" description="How SieveGuard complies with EU/UK GDPR — controller/processor model, sub-processors, data residency, and your rights." path="/gdpr" /><LegalLayout title="GDPR compliance" lastUpdated={c.lastUpdatedAt}>
      <p>
        SieveGuard is built for EU customers. This page summarises in plain English how we comply
        with the GDPR; the formal contractual commitment lives in the{' '}
        <Link to="/dpa">Data Processing Agreement</Link>.
      </p>

      <h2>The roles in one paragraph</h2>
      <p>
        When you submit emails to SieveGuard for screening, <strong>you are the Controller</strong>{' '}
        and <strong>SieveGuard is the Processor</strong>. We process those emails only on your
        instructions, only for the purposes you set, only for as long as you tell us. When
        we collect data about you (your account, your billing, your usage of our website),
        we are the Controller for that data and our{' '}
        <Link to="/privacy">Privacy Policy</Link> applies.
      </p>

      <h2>At a glance</h2>
      <ul>
        <li>
          <strong>Data residency</strong>: all detection + verification runs on{' '}
          <strong>{c.dataResidencyRegion}</strong>. Your data does not leave the EU.
        </li>
        <li>
          <strong>Encryption</strong>: TLS 1.3 in transit; AES-256 at rest.
        </li>
        <li>
          <strong>Tenant isolation</strong>: PostgreSQL Row-Level Security — your data is
          database-level isolated from every other customer.
        </li>
        <li>
          <strong>Retention</strong>: detection records kept for the lifetime of your
          subscription + <strong>{c.retentionDaysAfterTermination} days</strong>. Right to
          immediate purge from the portal.
        </li>
        <li>
          <strong>Sub-processors</strong>:{' '}
          {subprocessorsPage ? (
            <>
              published at <Link to="/subprocessors">/subprocessors</Link>;
            </>
          ) : (
            <>
              current list available on request to <code>{c.privacyEmail}</code>;
            </>
          )}{' '}
          30-day notice before any change.
        </li>
        <li>
          <strong>Breach notification</strong>: 72 hours from awareness.
        </li>
        <li>
          <strong>Data subject requests</strong>: forwarded to{' '}
          <code>{c.privacyEmail}</code> with assistance per GDPR.
        </li>
        <li>
          <strong>No advertising tracking</strong>: we do not run third-party analytics or
          marketing pixels on our website.
        </li>
      </ul>

      <h2>Signing the DPA</h2>
      <p>
        The <Link to="/dpa">DPA</Link> at <code>/dpa</code> is automatically incorporated
        into your Terms of Use when you subscribe — no separate signature needed for most
        customers. If your procurement team requires a counter-signed copy, email{' '}
        <code>{c.privacyEmail}</code> with your company details and we counter-sign within
        two business days.
      </p>

      <h2>Your rights</h2>
      <p>
        Under the GDPR you have the right to access, rectify, erase, restrict, port or
        object to the processing of your data, and to withdraw consent at any time where
        consent is the legal basis. Send any request to <code>{c.privacyEmail}</code>; we
        respond within 30 days.
      </p>

      <h2>If we get it wrong</h2>
      <p>
        You have the right to complain to a supervisory authority.{' '}
        {!c.hideBusinessIdentity && (
          <>
            For SieveGuard customers, the competent authority is the{' '}
            <a href="https://idpc.org.mt" target="_blank" rel="noreferrer">
              Maltese Office of the Information and Data Protection Commissioner (IDPC)
            </a>
            .{' '}
          </>
        )}
        You may also lodge a complaint with the supervisory authority in your country of
        residence.
      </p>

      <h2>Contact</h2>
      <p>
        GDPR / privacy: <code>{c.privacyEmail}</code> · General: <code>{c.supportEmail}</code>
        {!c.hideBusinessIdentity && (
          <>
            {' · '}Postal: {c.legalEntityName}, {c.registeredAddressLine}
          </>
        )}.
      </p>
    </LegalLayout></>
  );
}

// Default export required by React Router framework-mode route modules.
export default GdprPage;
